import * as React from 'react';
import { useMemo } from 'react';
import { TextDirection } from '../../../../localization/textDirection';
import { DirectoryRater, RaterLevel, AddedDirectoryRater } from '../models';
import LocalDiv from '../../../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../../../layout/flexBox';
import SearchDirectoryForm from './SearchDirectoryForm';
import RaterSearchResultList from './RaterSearchResultList';
import { css } from '@emotion/core';
import ClamShell from '../../../../layout/ClamShell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Msg from '../../../../localization/Msg';
import { Str } from '../../../../localization/translationKeys';
import { COLORS } from '../../../../globalStyle/colors';
import Spacer from '../../../../layout/Spacer';
import { nameOrEmail } from '../module';
import { Requirement } from '../../../models';


const styles = {
  root:          css`
    align-content: stretch;
  `,
  msgBar:        css`
    align-items: center;
    min-height: 60px;
    background-color: ${COLORS.WHITE};
    padding-left: 16px;
    padding-right: 16px;
  `,
  formContainer: css`
    align-items: center;
    background-color: ${COLORS.LIGHTEST_GREY};
    padding: 16px 4px;
  `,
  userMsg:       {
    root:    css`
      align-items: flex-end;
      font-size: 12px;
    `,
    icon:    css`
      font-size: 22px;
    `,
    msgText: css``,
  },
};


type Props = {
  className?: string;
  textDirection: TextDirection;
  raterLevels: RaterLevel[];
  searchResults: DirectoryRater[];
  maxedOutRequirement?: Requirement;
  setSearchTerm: (searchTerm: string) => void;
  lastAddedRater?: AddedDirectoryRater;
  addedRater?: AddedDirectoryRater;
  setAddedRater: (addedRater?: AddedDirectoryRater) => void;
  resultsPageSize: number;
  lastLoadedResultsPage: number;
  loadMoreResults: () => void;
  showSuccessMsg: boolean;
  showNoResultsMsg: boolean;
  showSearchErrorMsg: boolean;
  showAddErrorMsg: boolean;
};

/**
 * RaterSearchTable: a component that renders a message/feedback bar, search form, and result list for the directory
 * search feature.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterSearchTable: React.FC<Props> = props => {
  const raterNameOrEmail = useMemo(
    () => !!props.lastAddedRater ? nameOrEmail(props.textDirection, props.lastAddedRater) : '',
    [props.lastAddedRater, props.textDirection],
  );

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]}>
      <LocalDiv css={[FLEX_ROW, styles.msgBar]}>
        <ClamShell css={[FLEX_ROW, styles.userMsg.root]} show={props.showSuccessMsg}>
          <FontAwesomeIcon icon={faCheck} css={[styles.userMsg.icon, css`color: ${COLORS.BLUE}`]} size={'lg'} />
          <Spacer sizePx={10} />
          <Msg str={Str.NameAdded} replace={{ 'lastCommaFirst': raterNameOrEmail }} />
        </ClamShell>
        <ClamShell
          css={[FLEX_ROW, styles.userMsg.root, css`color: ${COLORS.RED}`]}
          show={props.showAddErrorMsg || props.showSearchErrorMsg}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} css={styles.userMsg.icon} size={'lg'} />
          <Spacer sizePx={10} />
          <Msg str={props.showAddErrorMsg ? Str.AddFailed : Str.UnknownError} />
        </ClamShell>
        <ClamShell
          css={[FLEX_ROW, styles.userMsg.root, css`color: ${COLORS.GREEN}`]}
          show={props.showNoResultsMsg}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} css={styles.userMsg.icon} size={'lg'} />
          <Spacer sizePx={10} />
          <Msg str={Str.NoResults} />
        </ClamShell>
        <ClamShell
          css={[FLEX_ROW, styles.userMsg.root, css`color: ${COLORS.RED}`]}
          show={!!props.maxedOutRequirement}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} css={styles.userMsg.icon} size={'lg'} />
          <Spacer sizePx={10} />
          <Msg str={Str.MaxRatersMsg} replace={{ 'levelName': props.maxedOutRequirement?.fullName || '' }} />
        </ClamShell>
      </LocalDiv>
      <LocalDiv css={[FLEX_ROW, styles.formContainer]}>
        <SearchDirectoryForm setSearchTerm={props.setSearchTerm} />
      </LocalDiv>
      <RaterSearchResultList
        dir={props.textDirection}
        raterLevels={props.raterLevels}
        searchResults={props.searchResults}
        setAddedRater={props.setAddedRater}
        resultsPageSize={props.resultsPageSize}
        lastLoadedResultsPage={props.lastLoadedResultsPage}
        loadMoreResults={props.loadMoreResults}
      />
    </LocalDiv>
  );
};

export default RaterSearchTable;
